import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { CalendarHeart } from "@phosphor-icons/react";

import EmptyAppointment from "assets/empty_appointment.png";
import { USER_ID_PARAM } from "@constants/calendarFilter";
import { ModuleContainer } from "../styles";
import ModuleHeader from "../ModuleHeader";

/**
 * @description `EmptyNextAppointment` component displays an empty next appointment when there are no scheduled appointments.
 * @param {Object} props - The component props.
 * @param {?string} props.clinicianName - The clinician name.
 * @param {?number} props.clinicianUserId - The clinician user id.
 * @returns {React.ReactNode} The EmptyNextAppointment component.
 */
const EmptyNextAppointment = ({ clinicianName, clinicianUserId }) => {
  let emptyAppointmentText =
    "It's quiet at the moment. Head over to your schedule to manage appointments.";
  let goToScheduleText = "Go to my schedule";
  let goToScheduleLink = "/clinician-schedule";

  if (clinicianName) {
    emptyAppointmentText = `It's quiet at the moment. Head over to ${clinicianName}'s schedule to manage appointments.`;
    goToScheduleText = `Go to schedule`;
    goToScheduleLink = `/clinician-schedule?${USER_ID_PARAM}=${clinicianUserId}`;
  }

  return (
    <Paper
      component="section"
      elevation={2}
      sx={ModuleContainer}
      data-testid="empty-next-appointment"
    >
      <ModuleHeader Icon={CalendarHeart} title="Next appointment" />
      <Stack direction={{ xs: "column", md: "row" }} spacing={7} mt={7} alignItems="center">
        <Box
          sx={{
            width: { xs: "100%", md: 223 },
            maxWidth: 223,
            mx: { xs: "auto", md: 0 },
            mb: { xs: 4, md: 0 },
          }}
        >
          <img
            src={EmptyAppointment}
            style={{ width: "100%", height: "auto", maxWidth: 223, display: "block" }}
            alt="Empty next appointment"
          />
        </Box>
        <Box>
          <Typography component="p" variant="body1" color="text.secondary">
            {emptyAppointmentText}
          </Typography>

          <Button
            variant="outlined"
            color="inherit"
            size="large"
            sx={{ mt: 7 }}
            href={goToScheduleLink}
          >
            {goToScheduleText}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default EmptyNextAppointment;
